import { ChevronLeft, Save, X } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/app-context';

type Unit = 'lbs' | 'kg';
type PlateWeight = number;

interface PlateConfig {
  weight: number;
  count: number;
  color: string;
}

export function PlateCalculator() {
  const navigate = useNavigate();
  const { state } = useApp();
  const [unit, setUnit] = useState<Unit>('lbs');
  const [targetWeight, setTargetWeight] = useState<number>(225);
  const [selectedPlates, setSelectedPlates] = useState<PlateConfig[]>([]);

  // Get bar weight from active gym profile or use defaults
  const barWeight = useMemo(() => {
    if (state.activeGymId) {
      const profile = state.gymProfiles.find((p) => p.id === state.activeGymId);
      if (profile && profile.barWeight) {
        return profile.barWeight;
      }
    }
    return unit === 'lbs' ? 45 : 20;
  }, [state.activeGymId, state.gymProfiles, unit]);

  // Get available plates from active gym profile or use defaults
  const availablePlates: { [key in Unit]: PlateWeight[] } = useMemo(() => {
    if (state.activeGymId) {
      const profile = state.gymProfiles.find((p) => p.id === state.activeGymId);
      if (profile && profile.availablePlates && profile.availablePlates.length > 0) {
        return {
          lbs: profile.availablePlates,
          kg: profile.availablePlates,
        };
      }
    }
    return {
      lbs: [45, 35, 25, 10, 5, 2.5],
      kg: [25, 20, 15, 10, 5, 2.5, 1.25],
    };
  }, [state.activeGymId, state.gymProfiles]);

  // Plate colors (standardized Olympic colors) - keyed by "unit-weight" to avoid duplicate keys
  const plateColorMap: { [key: string]: string } = {
    'lbs-45': '#E8432A',
    'lbs-35': '#FFA033',
    'lbs-25': '#4ADE80',
    'lbs-10': '#FFA033',
    'lbs-5': '#A855F7',
    'lbs-2.5': '#71717A',
    'kg-25': '#E8432A',
    'kg-20': '#2563EB',
    'kg-15': '#FFA033',
    'kg-10': '#52525B',
    'kg-5': '#A855F7',
    'kg-2.5': '#71717A',
    'kg-1.25': '#71717A',
  };
  const getPlateColor = (weight: number) => plateColorMap[`${unit}-${weight}`] || '#71717A';

  const calculatePlates = () => {
    const weightPerSide = (targetWeight - barWeight) / 2;
    const plates: PlateConfig[] = [];
    let remaining = weightPerSide;

    const sortedPlates = [...availablePlates[unit]].sort((a, b) => b - a);

    for (const plate of sortedPlates) {
      const count = Math.floor(remaining / plate);
      if (count > 0) {
        plates.push({
          weight: plate,
          count,
          color: getPlateColor(plate),
        });
        remaining -= count * plate;
      }
    }

    setSelectedPlates(plates);
  };

  const clearPlates = () => {
    setSelectedPlates([]);
    setTargetWeight(unit === 'lbs' ? 135 : 60);
  };

  const getTotalWeight = () => {
    const platesWeight = selectedPlates.reduce((sum, plate) => sum + plate.weight * plate.count * 2, 0);
    return barWeight + platesWeight;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#18181B', width: '390px', margin: '0 auto' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronLeft size={24} style={{ color: '#F0F0F0' }} />
        </button>

        <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>Plate Calculator</div>

        <div style={{ width: '44px' }} /> {/* Spacer */}
      </div>

      {/* Unit Tabs */}
      <div className="p-4">
        <div
          className="flex p-1 rounded-xl"
          style={{
            backgroundColor: '#27272A',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <button
            onClick={() => setUnit('lbs')}
            className="flex-1 py-2.5 rounded-lg transition-all"
            style={{
              backgroundColor: unit === 'lbs' ? '#FF6B35' : 'transparent',
              color: unit === 'lbs' ? '#18181B' : '#71717A',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            lbs
          </button>
          <button
            onClick={() => setUnit('kg')}
            className="flex-1 py-2.5 rounded-lg transition-all"
            style={{
              backgroundColor: unit === 'kg' ? '#FF6B35' : 'transparent',
              color: unit === 'kg' ? '#18181B' : '#71717A',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            kg
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Target Weight Input */}
        <div className="mb-6">
          <label
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              marginBottom: '8px',
              letterSpacing: '0.5px',
            }}
          >
            TARGET WEIGHT
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={targetWeight}
              onChange={(e) => setTargetWeight(Number(e.target.value))}
              className="flex-1 rounded-lg"
              style={{
                backgroundColor: '#27272A',
                color: '#F0F0F0',
                fontSize: '24px',
                fontWeight: 700,
                padding: '16px',
                border: '2px solid #FF6B35',
                textAlign: 'center',
              }}
            />
            <button
              onClick={calculatePlates}
              className="rounded-xl px-6"
              style={{
                backgroundColor: '#FF6B35',
                color: '#18181B',
                fontSize: '14px',
                fontWeight: 700,
                minHeight: '56px',
              }}
            >
              Calculate
            </button>
          </div>
        </div>

        {/* Visual Barbell */}
        {selectedPlates.length > 0 && (
          <div className="mb-6">
            <label
              style={{
                color: '#71717A',
                fontSize: '12px',
                fontWeight: 600,
                display: 'block',
                marginBottom: '12px',
                letterSpacing: '0.5px',
              }}
            >
              BARBELL VISUALIZATION
            </label>
            <div
              className="p-6 rounded-xl"
              style={{
                backgroundColor: '#27272A',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {/* Barbell SVG */}
              <div className="flex items-center justify-center">
                {/* Left plates */}
                <div className="flex items-center gap-1">
                  {selectedPlates.map((plate, idx) =>
                    Array.from({ length: plate.count }).map((_, i) => (
                      <div
                        key={`left-${idx}-${i}`}
                        className="rounded"
                        style={{
                          width: '8px',
                          height: `${Math.min(80, 20 + plate.weight * 1.2)}px`,
                          backgroundColor: plate.color,
                        }}
                      />
                    ))
                  )}
                </div>

                {/* Bar collar */}
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#71717A',
                    borderRadius: '2px',
                  }}
                />

                {/* Bar */}
                <div
                  style={{
                    width: '80px',
                    height: '6px',
                    backgroundColor: '#A1A1AA',
                  }}
                />

                {/* Center grip */}
                <div
                  style={{
                    width: '40px',
                    height: '10px',
                    backgroundColor: '#71717A',
                    borderRadius: '2px',
                  }}
                />

                {/* Bar */}
                <div
                  style={{
                    width: '80px',
                    height: '6px',
                    backgroundColor: '#A1A1AA',
                  }}
                />

                {/* Bar collar */}
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#71717A',
                    borderRadius: '2px',
                  }}
                />

                {/* Right plates */}
                <div className="flex items-center gap-1">
                  {selectedPlates.map((plate, idx) =>
                    Array.from({ length: plate.count }).map((_, i) => (
                      <div
                        key={`right-${idx}-${i}`}
                        className="rounded"
                        style={{
                          width: '8px',
                          height: `${Math.min(80, 20 + plate.weight * 1.2)}px`,
                          backgroundColor: plate.color,
                        }}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Total Weight Display */}
              <div className="text-center mt-4">
                <div style={{ color: '#71717A', fontSize: '12px', marginBottom: '4px' }}>Total Weight</div>
                <div
                  style={{
                    color: '#FF6B35',
                    fontSize: '32px',
                    fontWeight: 700,
                  }}
                >
                  {getTotalWeight()} {unit}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plate Breakdown */}
        {selectedPlates.length > 0 && (
          <div className="mb-6">
            <label
              style={{
                color: '#71717A',
                fontSize: '12px',
                fontWeight: 600,
                display: 'block',
                marginBottom: '8px',
                letterSpacing: '0.5px',
              }}
            >
              PLATES PER SIDE
            </label>
            <div className="space-y-2">
              {selectedPlates.map((plate, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg flex items-center justify-between"
                  style={{
                    backgroundColor: '#27272A',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="rounded"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: plate.color,
                      }}
                    />
                    <div>
                      <div
                        style={{
                          color: '#F0F0F0',
                          fontSize: '16px',
                          fontWeight: 700,
                        }}
                      >
                        {plate.weight} {unit}
                      </div>
                      <div style={{ color: '#71717A', fontSize: '12px' }}>{plate.count}x per side</div>
                    </div>
                  </div>
                  <div
                    style={{
                      color: '#71717A',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {plate.count * 2}x total
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Plate Buttons */}
        <div className="mb-6">
          <label
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              marginBottom: '8px',
              letterSpacing: '0.5px',
            }}
          >
            QUICK WEIGHTS
          </label>
          <div className="grid grid-cols-3 gap-2">
            {availablePlates[unit].map((weight) => (
              <button
                key={weight}
                onClick={() => {
                  setTargetWeight(barWeight + weight * 2);
                  setTimeout(calculatePlates, 100);
                }}
                className="p-4 rounded-lg flex flex-col items-center gap-2"
                style={{
                  backgroundColor: '#27272A',
                  border: '2px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  className="rounded"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: getPlateColor(weight),
                  }}
                />
                <div style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 700 }}>
                  {weight} {unit}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Bar Weight Info */}
        <div
          className="p-3 rounded-lg text-center"
          style={{
            backgroundColor: '#1F4428',
            border: '1px solid #4ADE80',
          }}
        >
          <div style={{ color: '#4ADE80', fontSize: '12px', fontWeight: 600 }}>
            Bar Weight: {barWeight} {unit}
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div
        className="p-4"
        style={{
          backgroundColor: '#27272A',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={clearPlates}
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'transparent',
              border: '2px solid #3F3F46',
              color: '#F0F0F0',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '52px',
            }}
          >
            <X size={18} />
            Clear
          </button>

          <button
            onClick={() => {
              navigate(-1);
            }}
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: '#FF6B35',
              color: '#18181B',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '52px',
            }}
          >
            <Save size={18} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
